import {Injectable} from '@angular/core';
import {defer, EMPTY, merge, Observable, of, Subject} from 'rxjs';
import {debounceTime, repeat, share, switchMapTo, takeUntil, tap} from 'rxjs/operators';
import {bindWith} from '../../../common/rxjs/bind-with';
import {dependOn} from '../../../common/rxjs/depend-on';
import {getPositionIdentificationKey, Position} from '../../../components/position';
import {SecurityService} from '../../../components/security';
import {
    UserDataStreamingService,
    UserDataWebSocketMessage,
    UserDataWebSocketMessageType
} from '../../../services/user-data-streaming';

// TODO: MARKET_COST_UPD
/*interface PositionMarketCostUpdate extends PartialPositionUpdate {
    marketCost: number;
}*/

interface PositionSnapshotWsMessage {
    messageType: UserDataWebSocketMessageType.POSITION_SNAPSHOT;
    data: Position[];
}

interface PositionUpdateWsMessage {
    messageType: UserDataWebSocketMessageType.POSITION;
    data: Position;
}

interface PositionRef {
    position: Position;
    index: number;
}

@Injectable()
export class PositionsStreamingService {

    /**
     * [DC-49] On Manticor we display closed positions of the current day.
     */
    public static isPositionDeprecated(position: Position): boolean {
        return !!position.closeDateTime;
    }

    // [ECN-48] Handle position with zero qty
    public static isPositionCloseable(position: Position): boolean {
        return !position.closeDateTime && !!position.quantity;
    }

    private static isPositionsSnapshotMessage(message: UserDataWebSocketMessage): message is PositionSnapshotWsMessage {
        return message.messageType === UserDataWebSocketMessageType.POSITION_SNAPSHOT;
    }

    private static isPositionUpdateMessage(message: UserDataWebSocketMessage): message is PositionUpdateWsMessage {
        return message.messageType === UserDataWebSocketMessageType.POSITION;
    }

    public readonly position$: Observable<Position>;
    public readonly positionUpdate$: Observable<Position>;

    /**
     * Solution against the bug: [ECN-261] Outdated positions after reconnect.
     * Since snapshots doesn't remove existing positions
     * (there are can be a few serial snapshots, one per account),
     * we need to remove all existing positions, when the very first snapshot came,
     * after the subscription sent.
     */
    public readonly cleanUpPositions$: Observable<void>;

    private positions: Position[] = [];
    private positionsDict: {[key: string]: PositionRef} = {};

    private readonly positionSubject = new Subject<Position>();

    private readonly cleanUpPositionsSubject = new Subject<void>();

    private readonly followUpPosition$: Observable<Position>;

    private doCleanUpPositionsOnNextSnapshot: boolean = false;

    constructor(
        private securityService: SecurityService,
        private userDataStreamingService: UserDataStreamingService
    ) {
        this.followUpPosition$ = this.positionSubject.pipe(
            bindWith(this.userDataStreamingService.messages$)
        );

        this.position$ = this.createPositionStream();
        this.positionUpdate$ = this.createPositionUpdatesStream();
        this.cleanUpPositions$ = this.createCleanUpPositionsStream();

        this.listenToUserData();
    }

    private readPositionsSnapshotMessage(snapshot: Position[]): void {
        if (this.doCleanUpPositionsOnNextSnapshot) {
            this.doCleanUpPositionsOnNextSnapshot = false;

            this.cleanUpPositions();
            this.cleanUpPositionsSubject.next();
        }

        snapshot.forEach(incomingPosition => this.readPositionMessage(incomingPosition));
    }

    private readPositionMessage(incomingPosition: Position): void {
        const positionKey = getPositionIdentificationKey(incomingPosition);
        const knownPositionRef: PositionRef = this.positionsDict[positionKey];

        const position = knownPositionRef
            ? {...knownPositionRef.position, ...incomingPosition}
            : incomingPosition;

        this.securityService.cacheSecurity(position.security);

        const isPositionDeprecated = PositionsStreamingService.isPositionDeprecated(position);

        if (knownPositionRef) {
            knownPositionRef.position = position;
            this.positions[knownPositionRef.index] = position;

            /**
             * Forget the position if it's deprecated now
             */
            if (isPositionDeprecated) {
                this.positions.splice(knownPositionRef.index, 1);
                delete this.positionsDict[positionKey];
                this.updatePositionsDictIndexesFrom(knownPositionRef.index);
            }
        } else if (!isPositionDeprecated) {
            this.positionsDict[positionKey] = {
                position,
                index: this.positions.push(position) - 1
            };
        }

        /**
         * Notify only if the position was known or it's new actual;
         * don't notify about unknown deprecated positions.
         */
        if (knownPositionRef || !isPositionDeprecated) {
            this.positionSubject.next(position);
        }
    }

    private updatePositionsDictIndexesFrom(indexUpdateFrom: number): void {
        for (let iPosition = indexUpdateFrom; iPosition < this.positions.length; iPosition++) {
            const positionKey = getPositionIdentificationKey(this.positions[iPosition]);

            this.positionsDict[positionKey].index = iPosition;
        }
    }

    private createPositionStream(): Observable<Position> {
        const initialOrders$ = defer((): Observable<Position> =>
            this.positions
                ? of(...this.positions)
                : EMPTY
        );

        return merge(
            initialOrders$,
            this.followUpPosition$
        );
    }

    private createPositionUpdatesStream(): Observable<Position> {
        const SNAPSHOT_TIME_LIMIT = 3000;

        return this.userDataStreamingService.subscriptionSent$.pipe(
            debounceTime(SNAPSHOT_TIME_LIMIT), // Wait some time to allow snapshot pass.
            switchMapTo(this.followUpPosition$),
            takeUntil(this.userDataStreamingService.disconnected$),
            repeat(),
            bindWith(this.userDataStreamingService.messages$),
            share()
        );
    }

    private listenToUserData(): void {
        this.userDataStreamingService.registerCoworker({
            handleMessage: message => {
                if (PositionsStreamingService.isPositionsSnapshotMessage(message)) {
                    this.readPositionsSnapshotMessage(message.data);
                } else if (PositionsStreamingService.isPositionUpdateMessage(message)) {
                    this.readPositionMessage(message.data);
                }
            },

            cleanUp: () => this.cleanUpPositions()
        });
    }

    private cleanUpPositions(): void {
        this.positions.length = 0;
        this.positionsDict = {};
    }

    private createCleanUpPositionsStream(): Observable<void> {
        const subscriptionSent$ = this.userDataStreamingService.subscriptionSent$.pipe(
            tap(() => {
                this.doCleanUpPositionsOnNextSnapshot = true;
            })
        );

        return this.cleanUpPositionsSubject.pipe(
            dependOn(subscriptionSent$)
        );
    }

}
